import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import { DataRoomCustomFolder } from "@/models/data-room/DataRoomCustomFolder";
import { DataRoomEntryType } from "@/models/data-room/DataRoomEntryType";
import { DataRoomEntry } from "@/pages/OneDataRoomPage";
import { InfoOutlined } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import menuClasses from "@mui/material/Menu/menuClasses";
import menuItemClasses from "@mui/material/MenuItem/menuItemClasses";
import Typography from "@mui/material/Typography";
import { SxProps, styled } from "@mui/material/styles";
import { Dispatch, SetStateAction, useState } from "react";
import { StyledInputField } from "../fundraising/advanced-search/AdvancedSearchFilters";
import CommonButton from "../utils/CommonButton";
import { FolderNotch } from "../utils/icons/FolderNotch";
import { ALL_ENTRIES_JOINED, DATA_ROOM_STATIC_ID } from "./DataRoomCategoriesDefines";
import { DataRoomCustomFoldersTree } from "./DataRoomCustomFoldersTree";
import { DataRoomRecursiveList } from "./DataRoomRecursiveList";
import { findName } from "./menu-utils/MenuEntryUtils";

type Props = {
  anchor: HTMLElement;
  setAnchor: Dispatch<SetStateAction<HTMLElement>>;
  onMoveFiles: (selectedMove: DataRoomEntry | DataRoomCustomFolder, type: DataRoomEntryType) => Promise<void>;
  moving?: boolean;
};

const debouncer = new Debouncer(200);

export function DataRoomMoveMenu(props: Props) {
  const [selectedMove, setSelectedMove] = useState<null | DataRoomEntry | DataRoomCustomFolder>(null);
  const [filteredMoves, setFilteredMoves] = useState<null | DATA_ROOM_STATIC_ID[]>([]);
  const [selectType, setSelectType] = useState<DataRoomEntryType>(DataRoomEntryType.STATIC);

  function handleSearch(inputSearch: string) {
    debouncer.executeLastCall(() => {
      if (inputSearch !== "") {
        let matches = [];
        findName(matches, inputSearch, ALL_ENTRIES_JOINED);
        setFilteredMoves(matches);
      } else {
        setFilteredMoves(null);
      }
    });
  }

  function handleMoveMenuClose() {
    props.setAnchor(null);
    setFilteredMoves(null);
    setSelectedMove(null);
  }

  function onMoveFiles() {
    props.onMoveFiles(selectedMove, selectType).then(() => handleMoveMenuClose());
  }

  return (
    <StyledMenu
      anchorEl={props.anchor}
      open={Boolean(props.anchor)}
      onClose={() => {
        setSelectedMove(null);
        handleMoveMenuClose();
      }}
    >
      <Box className="main-container">
        <Box className="textfield-container">
          <StyledInputField fullWidth onChange={(v) => handleSearch(v.currentTarget.value)} placeholder="Move file(s) to..." />
        </Box>
        <Box className="tree-container">
          {selectType === DataRoomEntryType.CUSTOM && (
            <DataRoomCustomFoldersTree level={0} selected={selectedMove as DataRoomCustomFolder} setSelected={setSelectedMove} />
          )}
          {selectType === DataRoomEntryType.STATIC && (
            <DataRoomRecursiveList
              entry={ALL_ENTRIES_JOINED}
              level={0}
              folderColors={{ 0: "#000" }}
              matches={filteredMoves}
              selected={selectedMove as DataRoomEntry}
              setSelected={setSelectedMove}
            />
          )}
        </Box>
        <Box className="footer-container">
          <Box display="flex" alignItems="center" width="100%" justifyContent="space-evenly">
            <Box onClick={() => setSelectType(DataRoomEntryType.STATIC)} sx={TreeSelectButtonSx(selectType === DataRoomEntryType.STATIC)}>
              <Typography fontSize="0.75rem">Common</Typography>
            </Box>
            <Box onClick={() => setSelectType(DataRoomEntryType.CUSTOM)} sx={TreeSelectButtonSx(selectType === DataRoomEntryType.CUSTOM)}>
              <Typography fontSize="0.75rem">Custom</Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap="0.25rem" width="100%">
            {selectedMove ? <FolderNotch /> : <InfoOutlined />}
            <Typography fontWeight="500" fontSize="0.875rem" maxWidth="100%" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
              {selectedMove?.name || "Please select a folder"}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap="0.75rem" width="100%">
            <CommonButton
              purpleBorderVariant
              style={{ borderRadius: "0.25rem", flex: 1, height: "1.75rem" }}
              onClick={() => {
                setSelectedMove(null);
                handleMoveMenuClose();
              }}
            >
              Cancel
            </CommonButton>
            <CommonButton
              sx={{ borderRadius: "0.25rem", flex: 1, height: "1.75rem" }}
              onClick={onMoveFiles}
              disabled={selectedMove === null || props.moving}
            >
              {!props.moving ? "Move" : <Spinner size={"1rem"} />}
            </CommonButton>
          </Box>
        </Box>
      </Box>
    </StyledMenu>
  );
}

const StyledMenu = styled(Menu)(() => ({
  top: 4,
  padding: 0,
  [`& .${menuClasses.list}`]: {
    padding: 0,
    height: "600px",
    width: "300px",
  },
  [`& .${menuClasses.paper}`]: {
    gap: "0.5rem",
    backgroundColor: "#fff",
    alignItems: "center",
    borderRadius: "0.5rem",
    boxShadow: "0 0 15px 0 rgba(0, 0, 0, 0.2)",
    padding: "0.75rem 0 0 0",
    [`& .${menuItemClasses.root}`]: {
      display: "flex",
      // padding: "0.375rem 0.75rem",
      gap: "0.5rem",
      alignItems: "center",
      alignSelf: "stretch",
      [`&.${menuItemClasses.focusVisible}`]: {
        backgroundColor: "transparent",
      },
    },
  },
  "& .main-container": {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    justifyContent: "flex-start",
    position: "relative",
    height: "100%",
    maxHeight: "100%",
    maxWidth: "100%",
    width: "100%",
    "& .tree-container": {
      padding: "0.25rem 0.75rem",
      maxHeight: "70%",
      overflow: "auto",
    },
    "& .textfield-container": {
      padding: "0.25rem 0.75rem",
    },
    "& .footer-container": {
      backgroundColor: "#fff",
      flexDirection: "column",
      padding: "0.5rem 0.75rem 0.75rem 0.75rem",
      width: "100%",
      display: "flex",
      gap: "0.75rem",
      position: "absolute",
      justifyContent: "space-between",
      bottom: 0,
      borderTop: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    },
  },
}));

const TreeSelectButtonSx: (selected: boolean) => SxProps = (selected: boolean) => ({
  cursor: "pointer",
  flex: 1,
  textAlign: "center",
  borderRadius: "0.25rem",
  ":hover": { backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE },
  ...(selected && { backgroundColor: Colors.BUTTON_SECONDARY_PURPLE }),
});
